import { ButtonHTMLAttributes, forwardRef, ElementType, ComponentPropsWithoutRef } from 'react';
import { cn } from '../../lib/cn';

interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

type ButtonAsButton = BaseButtonProps & 
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    as?: 'button';
  };

type ButtonAsOther<T extends ElementType> = BaseButtonProps & {
  as: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof BaseButtonProps>;

export type ButtonProps = ButtonAsButton | ButtonAsOther<ElementType>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      disabled,
      children,
      as: Component = 'button',
      ...rest
    } = props as ButtonAsButton & { as?: ElementType };

    const baseStyles =
      'inline-flex items-center justify-center font-medium transition-all duration-200 focus-ring rounded-lg disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 shadow-sm hover:shadow-md',
      secondary:
        'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700 shadow-sm hover:shadow-md',
      outline:
        'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 active:bg-primary-100',
      ghost:
        'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200',
      danger:
        'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 shadow-sm hover:shadow-md',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const computedClassName = cn(
      baseStyles,
      variants[variant],
      sizes[size],
      fullWidth && 'w-full',
      isLoading && 'cursor-wait',
      className
    );

    const loadingSpinner = isLoading && (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    if (Component !== 'button') {
      const Comp = Component as any;
      return (
        <Comp
          className={computedClassName}
          {...rest}
        >
          {loadingSpinner}
          {children}
        </Comp>
      );
    }

    return (
      <button
        ref={ref}
        className={computedClassName}
        disabled={disabled || isLoading}
        {...rest}
      >
        {loadingSpinner}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
